import React from 'react';

// Analysis runs (new primary types)
export type {
  AnalysisRun,
  AnalysisRunCreate,
  AnalysisRunUpdate,
  AnalysisRunBase,
  // Legacy aliases
  Project,
  ProjectCreate,
  ProjectUpdate,
  ProjectBase,
} from './analysisRun';

// User and Authentication Types
export interface User {
  id: string;
  email: string;
  created_at: string;
  is_admin?: boolean;
}

export interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  // Supabase AuthError.code (or message-derived fallback for 'Email not
  // confirmed'). Lives alongside `error` so UI can gate on a stable signal
  // rather than the friendly mapped string. Must be cleared on every
  // setAuthState that clears `error`.
  errorCode: string | null;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
}

export interface AuthResponse {
  user: User;
  access_token: string;
  token_type: string;
}

// Workflow Types
export enum WorkflowType {
  REVIEW = 'review',
  COMPARE = 'compare',
  NEGOTIATION = 'negotiation'
}

export interface WorkflowCard {
  id: string;
  name: string;
  description: string;
  useCases: string[];
  estimatedTime: string;
  icon: React.ReactNode;
  color: string;
  available: boolean;
}

// Project Types - now imported from analysisRun.ts
// Legacy CreateProjectData interface - kept for backward compatibility
export interface CreateProjectData {
  name: string;
  workflowType?: WorkflowType | string; // Frontend uses camelCase
  workflow_type?: WorkflowType | string; // Backend expects snake_case
  riskTolerance?: string;
  analysisDepth?: string;
  customInstructions?: string;
  comparisonType?: string;
  focusAreas?: string[];
  riskSensitivity?: string;
  negotiationStrategy?: string;
  priorityClauses?: string[];
  negotiationGoals?: string;
  counterpartyProfile?: string;
}

// Document Types
export interface Document {
  id: string;
  project_id: string;
  filename: string;
  file_path: string;
  file_type: string;
  file_size: number;
  document_role: string | null;
  extracted_text: string | null;
  upload_status: 'pending' | 'processing' | 'completed' | 'failed';
  created_at: string;
}

// API Response Types
export interface APIResponse<T = any> {
  data?: T;
  message?: string;
  error?: string;
  status: 'success' | 'error';
}

// Form Types
export interface FormErrors {
  [key: string]: string;
}

// Analysis Types
export interface AnalysisStatus {
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress?: number;
  message?: string;
}

export interface Issue {
  id: string;
  document_id: string;
  title: string;
  risk_level: 'high' | 'medium' | 'low';
  issue_type: string;
  description: string;
  document_references?: {
    document_section?: string;
    page_number?: number;
    clause_reference?: string;
    // Issue #335: clause_id populated from chunk's primary_clause_ids for overlay mapping
    clause_id?: string;
    chunk_id?: string;
    // Plan 191 / Issue #796: present on synthesised section-mode findings.
    // Always includes the primary clause_id; mirrors include sibling ids.
    // Length >= 1 when present (singletons get a length-1 list).
    merged_from_clause_ids?: string[];
  };
  ai_confidence: number;
  // Stage attribution fields for pipeline reasoning transparency (Task 47)
  source_stage?: string | null;
  /** @deprecated Use source_stage */
  source_agent?: string | null;  // Backward compatibility during transition
  chunk_index?: number | null;
  chunk_text?: string | null;
  original_ai_data?: {
    title?: string;
    description?: string;
    recommendation?: string;
    document_excerpt?: string;
    risk_analysis?: string;
    confidence?: number;
    document_section?: string;
    risk_level?: string;
    issue_type?: string;
    // Plan 113: AI reasoning transparency
    reasoning?: string;
    evidence?: {
      primary?: { logical_id?: string; heading?: string };
      neighbors?: Array<{ logical_id?: string; heading?: string }>;
      cross_refs?: Array<{ logical_id?: string; heading?: string }>;
      doc_type?: string;
      compare?: {
        similarity?: number;
        word_change_pct?: number;
      };
      scope?: {
        has_neighbors?: boolean;
        has_cross_refs?: boolean;
        has_doc_type?: boolean;
        is_full_document?: boolean;
      };
    };
    // Issue #231: document_references contains stage attribution and chunk context
    document_references?: {
      chunk_id?: string;
      chunk_index?: number;
      chunk_text?: string;
      stage_name?: string;
      /** @deprecated Use stage_name */
      agent_name?: string;  // Backward compatibility
      finding_type?: string;
      stage_confidence?: number;
      /** @deprecated Use stage_confidence */
      agent_confidence?: number;  // Backward compatibility
      created_at?: string;
      clause_id?: string;
      logical_id?: string;
      document_section?: string;
      clause_reference?: string;
      citations?: any[];
      // Plan 191 / Issue #796: mirror of top-level merged_from_clause_ids
      // for synthesised section-mode findings.
      merged_from_clause_ids?: string[];
    };
  };
  created_at: string;
  // Plan 180: per-finding soft delete metadata. Populated when an issue is
  // dismissed; null/undefined when active.
  dismissed_at?: string | null;
  dismissed_by?: string | null;
}

export interface DocumentIssues {
  document_id: string;
  document_name: string;
  document_size: number;
  issue_count: number;
  issues: Issue[];
}

export interface AnalysisResults {
  analysis_id: string;
  project_id: string;
  status: string;
  analysis_summary: string;
  total_issues: number;
  documents_analyzed: number;
  completed_at: string;
  issues_by_document: Record<string, DocumentIssues>;
  // 'multi_agent' and 'single_agent' deprecated, use 'pipeline' and 'single'
  analysis_method?: 'pipeline' | 'single' | 'multi_agent' | 'single_agent'; // Which analysis method was used
}

export interface IssueInteraction {
  issue_id: string;
  stage: 'original' | 'edited' | 'refined';
  action: 'accepted' | 'rejected' | 'edited' | 'pending';
  final_action?: 'accepted' | 'rejected';  // Final decision after editing/refining
  user_notes?: string;
  modified_title?: string;
  modified_description?: string;
  modified_recommendation?: string;
  modified_risk_level?: string;
  modified_risk_analysis?: string;
  modification_source?: 'user' | 'ai_refined';  // Track who made the modification
}

export interface ResultsViewState {
  expandedIssues: Set<string>;
  issueInteractions: Record<string, IssueInteraction>;
  selectedDocument?: string;
}

// Workflow Configuration Types
export interface WorkflowConfig {
  risk_tolerance: 'conservative' | 'moderate' | 'aggressive';
  analysis_depth: 'summary' | 'standard' | 'detailed';
  custom_instructions?: string;
  client_position?: 'supplier' | 'customer' | '';  // Issue #236: whose interests to protect
  // Backward compatibility fields
  target_areas?: string[];
  document_type?: string;
}

export interface WorkflowConfigData {
  risk_tolerance: string;
  analysis_depth: string;
  custom_instructions: string;
}

export interface ConfigurationFormData {
  risk_tolerance: 'conservative' | 'moderate' | 'aggressive';
  analysis_depth: 'summary' | 'standard' | 'detailed';
  custom_instructions: string;
  client_position?: 'supplier' | 'customer' | '';  // Issue #236: whose interests to protect
}

// Issue Management Types
export interface IssueModificationResponse {
  id: string;
  issue_id: string;
  modification_type: 'accepted' | 'rejected' | 'edited' | 'pending';
  stage?: 'original' | 'edited' | 'refined';
  modification_source?: 'user' | 'ai_refined';
  original_value?: string;
  modified_value?: string;
  user_notes?: string;
  user_comments?: string;
  created_at: string;
}

export interface IssueWithModifications extends Issue {
  user_modification?: IssueModificationResponse;
  is_modified: boolean;
  current_title: string;
  current_description: string;
  current_recommendation: string;
  current_risk_level: string;
  current_risk_analysis: string;
}

export interface IssueModificationSummary {
  total_issues: number;
  accepted_issues: number;
  rejected_issues: number;
  edited_issues: number;
  pending_issues: number;
  modification_rate: number;
}

// Pipeline Workflow Types for Task 48
export enum StageStatus {
  PENDING = 'pending',
  RUNNING = 'running',
  COMPLETED = 'completed',
  FAILED = 'failed'
}

/**
 * Execution status as stored in `workflow_executions.status`.
 *
 * Issue #995: this must mirror the backend `WorkflowExecutionStatus` enum, which
 * in turn mirrors the `workflow_executions_status_check` constraint. It was
 * missing `started`, `cancelled` and `fallback_required`, so a cancelled run had
 * no representable status on either side of the wire.
 *
 * UNKNOWN is a read-side sentinel the backend sends when it meets a value it does
 * not recognise — never write it.
 */
export enum WorkflowExecutionStatus {
  PENDING = 'pending',
  RUNNING = 'running',
  STARTED = 'started',
  /** Plan 243 / #990: results are being persisted. Active, not terminal. */
  FINALIZING = 'finalizing',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
  FALLBACK_REQUIRED = 'fallback_required',
  UNKNOWN = 'unknown',
}

export type WorkflowStatus =
  | 'pending'
  | 'running'
  | 'started'
  | 'finalizing'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'fallback_required'
  | 'unknown';

/** Statuses from which no further transition occurs. */
export const TERMINAL_EXECUTION_STATUSES: ReadonlySet<WorkflowStatus> = new Set<WorkflowStatus>([
  'completed',
  'failed',
  'cancelled',
]);

/**
 * Statuses from which the backend may still transition.
 *
 * Plan 243 Task 6.1 (#990). `finalizing` is ACTIVE: results are being persisted,
 * so the UI must keep polling. A component that treats it as terminal stops
 * polling mid-publication and shows a half-finished run as finished.
 *
 * This set exists because adding a status to the enum changed nothing:
 * `TERMINAL_EXECUTION_STATUSES` was referenced only inside this file, and every
 * polling decision in the app was a local literal. A status literal that stays
 * local is the next `finalizing`.
 */
export const ACTIVE_EXECUTION_STATUSES: ReadonlySet<WorkflowStatus> = new Set<WorkflowStatus>([
  'pending',
  'running',
  'started',
  'finalizing',
]);

export const isActiveExecutionStatus = (s?: string | null): boolean =>
  !!s && ACTIVE_EXECUTION_STATUSES.has(s as WorkflowStatus);

export const isTerminalExecutionStatus = (s?: string | null): boolean =>
  !!s && TERMINAL_EXECUTION_STATUSES.has(s as WorkflowStatus);

export interface StageExecutionInfo {
  stage_name: string;
  status: StageStatus;
  execution_time?: number;
  cost?: number;
  error_message?: string;
  output_summary?: string;
}

export interface PipelineStatusResponse {
  workflow_execution_id: string;
  project_id: string;
  status: WorkflowExecutionStatus;
  stage_results: { [key: string]: StageExecutionInfo };
  /** @deprecated Use stage_results */
  agent_results: { [key: string]: StageExecutionInfo };  // Backward compatibility - populated by backend during transition
  overall_progress: number;
  current_stage: string;
  total_cost?: number;
  execution_time?: number;
  fallback_used: boolean;
  fallback_reason?: string;
  fallback_statistics?: any;
  error_message?: string;
}

export interface WorkflowExecutionResponse {
  workflow_execution_id: string;
  project_id: string;
  status: WorkflowExecutionStatus;
  workflow_type: string;
  total_cost?: number;
  execution_time_ms?: number;
  fallback_used: boolean;
  stage_results: { [key: string]: any };
  /** @deprecated Use stage_results */
  agent_results: { [key: string]: any };  // Backward compatibility - populated by backend during transition
  created_at: Date;
  completed_at?: Date;
}

export interface PipelineAnalysisRequest {
  use_pipeline?: boolean;
  /** @deprecated Use use_pipeline */
  use_multi_agent?: boolean;  // Backward compatibility
  fallback_on_failure?: boolean;
  budget_limit?: number;
  timeout_seconds?: number;
}

export interface PipelineTraceResponse {
  project_id: string;
  workflow_execution_id: string;
  trace_data?: any;
  trace_available: boolean;
  trace_url?: string;
}

// Document Role Types for Compare Workflow
export type DocumentRoleType = 'base' | 'review';

export interface DocumentRole {
  role_id: string;
  document_id: string;
  role_type: DocumentRoleType;
  assigned_at: string;
  filename?: string;
  file_type?: string;
  file_size?: number;
}

export interface DocumentRoleAssignment {
  document_id: string;
  role_type: DocumentRoleType;
}

export interface DocumentRoleBulkAssignment {
  role_assignments: DocumentRoleAssignment[];
}

export interface DocumentRolesResponse {
  project_id: string;
  base_documents: DocumentRole[];
  review_documents: DocumentRole[];
  total_roles: number;
  has_base_document: boolean;
  has_review_documents: boolean;
}

export interface DocumentRoleValidationResponse {
  is_valid: boolean;
  project_id: string;
  base_document_count: number;
  review_document_count: number;
  total_documents: number;
  errors: string[];
}

export interface DocumentRoleAssignmentResult {
  success: boolean;
  action: 'assigned' | 'updated';
  role_id: string;
  document_id: string;
  role_type: DocumentRoleType;
  assigned_at: string;
}

export interface DocumentRoleBulkAssignmentResult {
  success: boolean;
  project_id: string;
  assignments: {
    role_id: string;
    document_id: string;
    role_type: DocumentRoleType;
    assigned_at: string;
  }[];
  total_assigned: number;
}

// Compare Workflow Configuration Types for Task 71
export type ComparisonType = 'clause-by-clause' | 'semantic' | 'structural';
export type ComparisonRiskSensitivity = 'low' | 'standard' | 'high';
export type ComparisonDepth = 'summary' | 'standard' | 'detailed';

export interface CompareWorkflowConfig {
  comparison_type: ComparisonType;
  focus_areas: string[];
  risk_sensitivity: ComparisonRiskSensitivity;
  comparison_depth: ComparisonDepth;
  include_formatting_changes: boolean;
  minimum_similarity_threshold: number;
  enable_clause_alignment: boolean;
  detect_moved_clauses: boolean;
  // Base configuration fields
  risk_tolerance: string;
  analysis_depth: string;
  custom_instructions: string;
  target_areas: string[];
  document_type: string;
  privacy_level: string;
  preferred_provider: string;
}

export interface CompareWorkflowConfigResponse extends CompareWorkflowConfig {
  id: string;
  project_id: string;
  created_at: string;
}

// Compare Document Upload Types for Task 72
export interface CompareUploadingDocument {
  file: File;
  progress: number;
  status: 'uploading' | 'completed' | 'error';
  error?: string;
  id: string;
  assignedRole: DocumentRoleType | 'unassigned';
}

export interface CompareUploadSettings {
  autoAssignBase: boolean;
  defaultReviewRole: boolean;
}

export interface BatchUploadProgress {
  totalDocuments: number;
  completedDocuments: number;
  failedDocuments: number;
  uploadingDocuments: number;
  overallProgress: number;
}

// Backward compatibility aliases (deprecated, remove in Phase 20)
/** @deprecated Use StageStatus */
export type AgentStatus = StageStatus;
/** @deprecated Use StageExecutionInfo */
export type AgentExecutionInfo = StageExecutionInfo;
/** @deprecated Use PipelineStatusResponse */
export type MultiAgentStatusResponse = PipelineStatusResponse;
/** @deprecated Use PipelineAnalysisRequest */
export type MultiAgentAnalysisRequest = PipelineAnalysisRequest;
/** @deprecated Use PipelineTraceResponse */
export type MultiAgentTraceResponse = PipelineTraceResponse;

// Matter Layer Types - Re-export from matter.ts
export * from './matter';